import { useFetcher } from '@remix-run/react';
import { useEffect } from 'react';
import { useProductTagging } from '../context';
import { useToaster } from '~/modules/root/context/ToasterContext';
import generateFetcherKey from '~/utils/generate-fetcher-key';

const useDeleteTag = (
    imageId: number,
    listProductTags: number[],
): {
    deleteTag: (tagId: number) => void;
} => {
    const fetcher = useFetcher<never>();
    const { dispatch } = useProductTagging();
    const { toast } = useToaster({ autoHideDelay: 3 });

    useEffect(() => {
        if (fetcher.state === 'loading') {
            if (
                fetcher.data &&
                'error' in fetcher.data &&
                fetcher.data['error']
            ) {
                toast('Failed to delete tag, please try again later.');
            } else {
                toast('Tag deleted');
            }
        }
    }, [fetcher.data, fetcher.state, toast]);

    useEffect(() => {
        if (fetcher.state === 'loading') {
            dispatch({ type: 'TOGGLE_DELETING_TAG', payload: false });
        } else if (
            fetcher.state === 'submitting' &&
            fetcher?.formData?.get('tagId')
        ) {
            dispatch({ type: 'TOGGLE_DELETING_TAG', payload: true });
        }
    }, [dispatch, fetcher]);

    const deleteTag = (tagId: number) => {
        const formData = new FormData();
        formData.set('libraryImageID', String(imageId));
        formData.set('tagId', String(tagId));
        // There can be multiple tags with the same product
        // used for the optimistic results, to avoid deleting from the product list knowing the product is on another tag
        const isUniqueProduct =
            listProductTags.filter((id) => id === tagId).length <= 1;
        formData.set('isUniqueProduct', String(isUniqueProduct));
        formData.set('fetcherKey', generateFetcherKey());
        fetcher.submit(formData, {
            method: 'post',
            encType: 'multipart/form-data',
            action: '/remix-api/product-tagging/delete-library-image-marker',
        });
    };

    return {
        deleteTag,
    };
};

export default useDeleteTag;
